import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguageData } from '../contexts/LanguageContext';
import './PageHeader.css';

const PageHeader = ({ 
  breadcrumbs = [], 
  language, 
  proficiencyLevel, 
  onLanguageChange, 
  onLevelChange,
  rightContent,
  disableDropdowns = false,
  disableLevelDropdown = false,
  // showProgress = false,
  // progressPercent = 0,
  // progressText = ''
}) => {
  const navigate = useNavigate();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const langDropdownRef = useRef(null);
  const levelDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setShowLanguageDropdown(false);
      }
      if (levelDropdownRef.current && !levelDropdownRef.current.contains(e.target)) {
        setShowLevelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Use shared language data from context
  const { languages: languagesData, proficiencyLevels: levelsData, fetchLevelsForLanguage } = useLanguageData();

  // Fetch levels when language changes
  React.useEffect(() => {
    if (language) {
      fetchLevelsForLanguage(language);
    }
  }, [language, fetchLevelsForLanguage]);

  // Auto-select first active level when levels change (e.g., after language switch)
  const prevLanguageRef = React.useRef(language);
  React.useEffect(() => {
    if (!levelsData || Object.keys(levelsData).length === 0) return;
    
    const languageChanged = prevLanguageRef.current !== language;
    prevLanguageRef.current = language;
    
    // Check if current proficiencyLevel exists in the new levels
    const currentExists = proficiencyLevel && levelsData[proficiencyLevel];
    
    if ((!currentExists || languageChanged) && onLevelChange) {
      // Find the first active level
      const firstActiveKey = Object.entries(levelsData).find(
        ([, data]) => (typeof data === 'object' ? data.active !== false : true)
      );
      if (firstActiveKey) {
        onLevelChange(firstActiveKey[0]);
      }
    }
  }, [levelsData, proficiencyLevel, onLevelChange, language]);

  // Transform data for display
  const languages = Object.entries(languagesData).map(([code, data]) => ({
    value: code,
    label: data.label,
    flag: data.flag || `/images/flag-${code}.svg`,
    active: data.active !== false,
  }));

  const proficiencyLevels = Object.entries(levelsData).map(([key, data]) => ({
    value: key,
    label: typeof data === 'string' ? data : (typeof data?.label === 'string' ? data.label : key),
    active: typeof data === 'object' ? data.active !== false : true,
  }));

  const selectedLanguage = languages.find(lang => lang.value === language) || languages[0] || { label: 'German', flag: '/images/flag-german.svg', active: true };
  const selectedLevel = proficiencyLevels.find(level => level.value === proficiencyLevel) || proficiencyLevels[0] || { label: 'Beginner A1', active: true };
  
  // Ensure selectedLevel.label is always a string
  const selectedLevelLabel = typeof selectedLevel?.label === 'string' ? selectedLevel.label : 'Beginner A1';

  const handleLanguageChange = (lang) => {
    if (lang.active) {
      setShowLanguageDropdown(false);
      if (onLanguageChange) {
        onLanguageChange(lang.value);
      }
    }
  };

  const handleLevelChange = (level) => {
    if (level.active) {
      setShowLevelDropdown(false);
      if (onLevelChange) {
        onLevelChange(level.value);
      }
    }
  };

  return (
    <div className="page-header-bar">
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="breadcrumb-separator">/</span>}
            <span 
              className={`breadcrumb-item ${crumb.current ? 'current' : ''}`}
              onClick={() => {
                if (crumb.current) return;
                if (crumb.onClick) { crumb.onClick(); return; }
                if (crumb.path) navigate(crumb.path, { state: { language, proficiencyLevel } });
              }}
            >
              {crumb.label}
            </span>
          </React.Fragment>
        ))}
      </div>
      
      {rightContent ? (
        rightContent
      ) : (
        <div className="header-right-section">
          <div className="language-badges">
            <div className="badge-dropdown" ref={langDropdownRef} style={disableDropdowns ? {opacity: 0.3, pointerEvents: 'none'} : {}} onClick={() => {
              setShowLanguageDropdown(!showLanguageDropdown);
              setShowLevelDropdown(false);
            }}>
              <img src={selectedLanguage.flag} alt={`${selectedLanguage.label} flag`} className="flag-icon" />
              <span>{selectedLanguage.label}</span>
              <img 
                src="/images/dropdownarrow.svg" 
                alt="Dropdown" 
                className={`dropdown-arrow-icon ${showLanguageDropdown ? 'open' : ''}`}
              />
              {showLanguageDropdown && (
                <div className="dropdown-menu">
                  {languages.map((lang) => (
                    <div 
                      key={lang.value} 
                      className={`dropdown-item ${language === lang.value ? 'active' : ''} ${!lang.active ? 'inactive' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLanguageChange(lang);
                      }}
                    >
                      <img src={lang.flag} alt={`${lang.label} flag`} className="dropdown-flag" />
                      <span>{lang.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="badge-dropdown" ref={levelDropdownRef} style={(disableDropdowns || disableLevelDropdown) ? {opacity: 0.3, pointerEvents: 'none'} : {}} onClick={() => {
              setShowLevelDropdown(!showLevelDropdown);
              setShowLanguageDropdown(false);
            }}>
              <span>{selectedLevelLabel}</span>
              <img 
                src="/images/dropdownarrow.svg" 
                alt="Dropdown" 
                className={`dropdown-arrow-icon ${showLevelDropdown ? 'open' : ''}`}
              />
              {showLevelDropdown && (
                <div className="dropdown-menu">
                  {proficiencyLevels.map((level) => (
                    <div 
                      key={level.value} 
                      className={`dropdown-item ${proficiencyLevel === level.value ? 'active' : ''} ${!level.active ? 'inactive' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLevelChange(level);
                      }}
                    >
                      <span>{level.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* {showProgress && (
            <div className="header-progress-section">
              <div className="header-progress-bar">
                <div className="header-progress-fill" style={{ width: `${progressPercent}%` }}></div>
              </div>
              <div className="header-progress-info">
                <span className="header-progress-percent">{progressPercent}%</span>
                <span className="header-progress-text">{progressText}</span>
              </div>
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
